import { useState, useEffect, useCallback, useRef } from 'react';
import {
  subscribeToNotifications,
  getNotifications,
  markAsRead,
  markAllAsRead
} from '../services/notificationService';

/**
 * Hook para notificaciones in-app del usuario (tiempo real)
 * @param {string} userId - ID del usuario (currentUser.uid)
 * @returns {object} { notifications, unreadCount, loading, loadMore, hasMore, markAsRead, markAllAsRead, refresh }
 */
export const useNotifications = (userId) => {
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [olderNotifications, setOlderNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const lastVisibleRef = useRef(null);
  const olderLastVisibleRef = useRef(null);

  const notifications = [...recentNotifications, ...olderNotifications];

  useEffect(() => {
    if (!userId) {
      setRecentNotifications([]);
      setOlderNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const unsubscribe = subscribeToNotifications(userId, (data) => {
      setRecentNotifications(data.notifications);
      setUnreadCount(data.unreadCount);
      lastVisibleRef.current = data.lastVisible;
      setHasMore(data.notifications.length === 20);
      setLoading(false);
    });
    return () => {
      unsubscribe();
    };
  }, [userId]);

  const loadMore = useCallback(async () => {
    if (!userId || loading) return;
    const startAfter = olderNotifications.length > 0 ? olderLastVisibleRef.current : lastVisibleRef.current;
    if (!startAfter && olderNotifications.length > 0) return;
    setLoading(true);
    const result = await getNotifications(userId, 20, startAfter);
    if (result.success && result.notifications.length > 0) {
      setOlderNotifications(prev => [...prev, ...result.notifications]);
      olderLastVisibleRef.current = result.lastVisible;
      setHasMore(result.hasMore || false);
    } else {
      setHasMore(false);
    }
    setLoading(false);
  }, [userId, loading, olderNotifications.length]);

  const handleMarkAsRead = useCallback(async (notificationId) => {
    const result = await markAsRead(notificationId);
    if (result.success) {
      setRecentNotifications(prev =>
        prev.map(n => (n.id === notificationId ? { ...n, read: true } : n))
      );
      setOlderNotifications(prev =>
        prev.map(n => (n.id === notificationId ? { ...n, read: true } : n))
      );
      setUnreadCount(c => Math.max(0, c - 1));
    }
  }, []);

  const handleMarkAllAsRead = useCallback(async () => {
    if (!userId) return;
    const result = await markAllAsRead(userId);
    if (result.success) {
      setRecentNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setOlderNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    }
  }, [userId]);

  const refresh = useCallback(() => {
    setOlderNotifications([]);
    olderLastVisibleRef.current = null;
    setHasMore(false);
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    loadMore,
    hasMore,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    refresh
  };
};
